import { useState } from "react";
import PublicShell, { useSiteContent } from "@/components/layout/PublicShell";
import { api, formatApiError } from "@/lib/api";
import { useFormSecurity } from "@/lib/useFormSecurity";
import { CheckCircle2 } from "lucide-react";

export default function EngagePage() {
  const content = useSiteContent();
  const copy = content?.engage || {};
  const { getSignals } = useFormSecurity();

  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    phone: "",
    description: "",
    website: "", // honeypot — must remain empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, ...getSignals() });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicShell>
      <section className="border-b border-border min-h-[60vh]" data-testid="engage-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Engage Nexra</div>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.02] text-obsidian">
              {copy.title || "Engage Nexra"}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-lg">
              {copy.subtitle || "Tell us about the delivery situation. Nexra will review your request and be in contact."}
            </p>
            <ul className="mt-10 space-y-3 text-sm text-foreground/70">
              <li className="flex gap-3"><span className="text-execution font-mono">→</span> A senior delivery lead reviews every enquiry.</li>
              <li className="flex gap-3"><span className="text-execution font-mono">→</span> Typical response within two business days.</li>
              <li className="flex gap-3"><span className="text-execution font-mono">→</span> Confidential. No junior gatekeepers.</li>
            </ul>
          </div>

          <div className="lg:col-span-7" data-testid="engage-form-container">
            {done ? (
              <div
                className="border border-border p-10 lg:p-14 bg-muted"
                data-testid="engage-confirmation"
              >
                <CheckCircle2 className="h-8 w-8 text-execution mb-6" />
                <h2
                  className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight"
                  data-testid="engage-success-title"
                >
                  {copy.success_title || "Thank you. Nexra has received your enquiry."}
                </h2>
                <p
                  className="mt-4 text-base text-muted-foreground leading-relaxed max-w-xl"
                  data-testid="engage-success-body"
                >
                  {copy.success_body || "A member of the Nexra team will review your request and be in contact shortly. No further action is required from you at this stage."}
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="border border-border p-5 sm:p-6 lg:p-10 bg-white"
                data-testid="engage-form"
              >
                {/* Honeypot — hidden from humans, visible to bots */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label>Website</label>
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Name" required>
                    <input
                      className="input-swiss"
                      required
                      autoComplete="name"
                      value={form.name}
                      onChange={update("name")}
                      data-testid="engage-input-name"
                    />
                  </Field>
                  <Field label="Organisation" required>
                    <input
                      className="input-swiss"
                      required
                      autoComplete="organization"
                      value={form.organisation}
                      onChange={update("organisation")}
                      data-testid="engage-input-organisation"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      className="input-swiss"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      required
                      value={form.email}
                      onChange={update("email")}
                      data-testid="engage-input-email"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      className="input-swiss"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      required
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="engage-input-phone"
                    />
                  </Field>
                </div>
                <Field className="mt-5" label="Describe the delivery situation" required>
                  <textarea
                    className="input-swiss min-h-[160px] resize-y"
                    required
                    value={form.description}
                    onChange={update("description")}
                    data-testid="engage-input-description"
                  />
                </Field>
                {error && (
                  <div className="mt-4 text-sm text-destructive" data-testid="engage-error">
                    {error}
                  </div>
                )}
                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                    Confidential submission
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-60 w-full sm:w-auto"
                    data-testid="engage-submit"
                  >
                    {submitting ? "Sending..." : "Submit request"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
        {label}
        {required && <span className="text-execution"> *</span>}
      </span>
      {children}
    </label>
  );
}
